import { Component, Input, Output, EventEmitter, OnChanges, ElementRef, SimpleChanges, Optional, Inject, forwardRef, OnInit, OnDestroy } from "@angular/core";
import { IVideoConfigSource } from "@app/interfaces/IVideo";
import { TranslationService } from "@app/services/translation.service";
import Hls from "hls.js";
import { IVideoSectionLoaded } from "./interfaces/IVideoSectionLoaded";
import './v-video.component.scss';

@Component({
    selector: 'v-video',
    template: require('./v-video.component.html')
})
export class VVideoComponent implements OnChanges, OnInit, OnDestroy {
    @Input() sources: IVideoConfigSource[] = [];
    @Input() poster: string;
    @Input() controls: boolean;
    @Input() mp4VideoUrl: string;
    @Input() webmVideoUrl: string;
    @Input() m3u8VideoUrl: string;
    @Input() playbackSpeed = 1;
    @Input() videoLabel: string;
    @Input() currentPosition: number;
    @Input() autoplay: boolean;

    @Output() timeUpdate = new EventEmitter<number>();
    @Output() load = new EventEmitter<void>();
    @Output() canPlay = new EventEmitter<void>();
    @Output() play = new EventEmitter<void>();
    @Output() pause = new EventEmitter<void>();
    @Output() seeking = new EventEmitter<number>();
    @Output() seeked = new EventEmitter<number>();
    @Output() sectionLoaded = new EventEmitter<IVideoSectionLoaded[]>();
    @Output() error = new EventEmitter<any>();

    //--

    loading: boolean;
    videoElementId: number;
    videoPlayer: HTMLVideoElement;
    streamPaused: boolean;
    showError: boolean;

    timeout: ReturnType<typeof setTimeout>;

    private hlsPlayer: Hls;
    constructor(
        private readonly el: ElementRef,
        private readonly translationService: TranslationService) { }

    ngOnInit(): void {
        this.createVideoElements();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.mp4VideoUrl) {
            this.addVideoSource("mp4");
        }

        if (changes["playbackSpeed"] && this.videoPlayer) {
            this.videoPlayer.playbackRate = this.playbackSpeed;
        }
    }

    ngOnDestroy(): void {
        this.destroyPlayer();
        this.destroyVideoElement();
        clearTimeout(this.timeout);
    }

    playVideo(): void {
        this.streamPaused = false;
        this.videoPlayer.play();
    }

    pauseVideo(): void {
        this.streamPaused = true;
        this.videoPlayer.pause();

        if (this.hlsPlayer) {
            this.hlsPlayer.stopLoad();
        }
    }

    restartVideo(): void {
        this.videoPlayer.currentTime = 0;
        this.videoPlayer.pause();

        if (this.hlsPlayer) {
            this.hlsPlayer.startLoad();
            this.startPauseStreamTimer();
        }
    }

    seek(time: number): void {
        this.videoPlayer.currentTime = time;
    }

    getDuration(): number {
        return this.videoPlayer.duration;
    }

    isPlaying(): boolean {
        return !this.videoPlayer.paused;
    }

    getCurrentTime(): number {
        return this.videoPlayer?.currentTime;
    }

    //--

    private addVideoSource(contentType: string): void {
        this.sources.push({
            src: this.mp4VideoUrl,
            type: `video/${contentType}`,
            contentType: contentType
        })
    }

    private createVideoElements(): void {
        this.loading = true;

        this.videoElementId = Math.floor(Math.random() * 1000);
        this.videoPlayer = document.createElement('video');

        this.videoPlayer.id = String(this.videoElementId);
        this.videoPlayer.autoplay = this.autoplay;
        this.videoPlayer.controls = this.controls;
        this.videoPlayer.playsInline = true;
        this.videoPlayer.muted = true;
        this.videoPlayer.poster = this.poster;
        this.videoPlayer.innerHTML = this.translationService.t('veriVideo.browserNotSupported');

        if (this.sources?.length && !this.m3u8VideoUrl) {
            this.sources.forEach(source => {
                const videoSource = document.createElement('source');
                videoSource.src = `${source.src}/${source.contentType}`;
                videoSource.type = source.type;
                this.videoPlayer.appendChild(videoSource);
            });
        }

        this.el.nativeElement.appendChild(this.videoPlayer);

        if (this.m3u8VideoUrl) {
            this.setupStreamPlayer();
        }

        this.videoPlayer.onloadedmetadata = () => this.videoLoaded();
        this.videoPlayer.oncanplay = () => this.canPlay.emit();
        this.videoPlayer.onplay = () => this.play.emit();
        this.videoPlayer.onpause = () => this.pause.emit();
        this.videoPlayer.onseeked = () => this.seeked.emit(this.videoPlayer.currentTime);
        this.videoPlayer.onprogress = () => this.videoProgress();

    }

    private videoLoaded(): void {
        this.load.emit();
        this.loading = false;
    }

    private videoProgress(): void {
        const seekable: TimeRanges = (this.videoPlayer as any).buffered;
        const sections: IVideoSectionLoaded[] = [];

        for (var si = 0; si < seekable.length; si++) {
            var startSecond = seekable.start(si);
            var endSecond = seekable.end(si);

            sections.push({ startSecond: startSecond, endSecond: endSecond });
        }

        this.sectionLoaded.emit(sections);
    }

    private startPauseStreamTimer(): void {
        if (this.timeout) {
            clearTimeout(this.timeout);
        }

        this.timeout = setTimeout(() => {
            this.pauseVideo();
        }, 60 * 1000);
    }

    private destroyPlayer(): void {
        if (!this.hlsPlayer) {
            return;
        }

        this.hlsPlayer.detachMedia();
        this.hlsPlayer.destroy();

        this.videoPlayer = null;
    }

    private destroyVideoElement(): void {
        if (this.videoPlayer) {
            // Pause the video if it's playing
            if (!this.videoPlayer.paused) {
                this.videoPlayer.pause();
            }

            // Remove event listeners
            this.videoPlayer.onloadedmetadata = null;
            this.videoPlayer.oncanplay = null;
            this.videoPlayer.onplay = null;
            this.videoPlayer.onpause = null;
            this.videoPlayer.onseeked = null;
            this.videoPlayer.onprogress = null;

            // Remove the video element from the DOM
            if (this.videoPlayer.parentNode) {
                this.videoPlayer.parentNode.removeChild(this.videoPlayer);
            }

            // Nullify the reference
            this.videoPlayer = null;
        }

    }

    private setupStreamPlayer(): void {
        this.destroyPlayer();

        this.hlsPlayer = new Hls({
            debug: false,
            enableWebVTT: false,
            liveDurationInfinity: true,
            autoStartLoad: true,
            enableWorker: true,
            capLevelToPlayerSize: true,
            startFragPrefetch: true,
            fragLoadingTimeOut: 60 * 1000
        });

        this.hlsPlayer.attachMedia(this.videoPlayer);

        this.hlsPlayer.on(Hls.Events.MEDIA_ATTACHED, () => {
            this.hlsPlayer.loadSource(this.m3u8VideoUrl);
        });

        this.hlsPlayer.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
            this.videoPlayer.play();
        });

        this.hlsPlayer.once(Hls.Events.FRAG_LOADED, () => {
            this.startPauseStreamTimer();
        });

        this.hlsPlayer.on(Hls.Events.ERROR, (event, data) => {
            if (data.fatal) {
                switch (data.type) {
                    case Hls.ErrorTypes.NETWORK_ERROR:
                        // try to recover network error
                        console.warn("fatal network error encountered, try to recover");
                        this.hlsPlayer.startLoad();
                        break;

                    case Hls.ErrorTypes.MEDIA_ERROR:
                        console.warn("fatal media error encountered, try to recover");
                        this.hlsPlayer.recoverMediaError();
                        break;

                    default:
                        // cannot recover
                        this.hlsPlayer.destroy();
                        this.error.emit(data);

                        console.error("Unrecoverable error loading video")

                        this.showError = true;
                        break;
                }
            }
        });
    }

}
